import React, { useMemo, useState } from 'react';
import { Batiment, Vehicule } from '../../../../shared/types/game';
import { type InstanceRow } from '../../components/interventions/instanceModel';
import { type LiveVehicleTrack } from '../../components/WorldMap';
import { useVehicleCrewStatus } from '../hooks/useVehicleCrewStatus';
import { useStatusPalette } from '../hooks/useStatusPalette';
import { useEngagementGroups } from '../hooks/useEngagementGroups';
import { unitTypeLabel } from '../mapUtils';

/**
 * Moniteur d'engins — portage de l'habillage du prototype (`units-panel`).
 *
 * Le prototype place ce tableau AU CENTRE, sous la carte, et non dans une colonne
 * latérale : c'est la pièce maîtresse du dispatch. Colonnes reprises telles quelles —
 * Indicatif, Type, Statut, Affectation, Équipage, Remise, Délai — la colonne
 * **Équipage** portant l'armement en ligne, avec le motif en infobulle.
 */

/** Palette du prototype (styles.css `:root`). */
const P = {
  panel: '#0e1522',
  panel2: '#121b2b',
  panel3: '#172234',
  line: '#223046',
  lineSoft: '#1a2537',
  text: '#dce5f2',
  text2: '#9fb0c6',
  muted: '#6b7d95',
  green: '#10b981',
  red: '#ef4444',
  amber: '#f59e0b',
  blue: '#38bdf8',
};
const MONO = "'JetBrains Mono','Cascadia Mono','Consolas','SF Mono',monospace";

interface Props {
  playerVehicles: Vehicule[];
  buildings: Batiment[];
  interventions: InstanceRow[];
  liveVehicleTracks: LiveVehicleTrack[];
}

type StatusFilter = 'all' | 'free' | 'busy' | 'ko';

const UnitsMonitorTable: React.FC<Props> = ({
  playerVehicles,
  buildings,
  interventions,
  liveVehicleTracks,
}) => {
  const [search, setSearch] = useState('');
  const [buildingFilter, setBuildingFilter] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { vehicles: crewStatus, staffById } = useVehicleCrewStatus();
  const { palette } = useStatusPalette();
  const { groups } = useEngagementGroups();

  const crewById = useMemo(() => {
    const m = new Map<number, (typeof crewStatus)[number]>();
    for (const c of crewStatus) m.set(Number(c.vehicleId), c);
    return m;
  }, [crewStatus]);

  const buildingById = useMemo(() => {
    const m = new Map<number, Batiment>();
    for (const b of buildings || []) m.set(Number(b.id), b);
    return m;
  }, [buildings]);

  /** Intervention sur laquelle l'engin est engagé, si connue. */
  const assignmentOf = useMemo(() => {
    const m = new Map<number, InstanceRow>();
    for (const inv of interventions || []) {
      const ids = (inv as unknown as { dispatch_player_vehicle_ids?: unknown[] })
        .dispatch_player_vehicle_ids;
      if (!Array.isArray(ids)) continue;
      for (const id of ids) m.set(Number(id), inv);
    }
    return m;
  }, [interventions]);

  const etaOf = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const t of liveVehicleTracks || []) m.set(Number(t.vehicle_id), t.eta_remaining_sec ?? null);
    return m;
  }, [liveVehicleTracks]);

  const effStatus = (v: Vehicule) => {
    if (v.statut === 'disponible') {
      return crewById.get(Number(v.id))?.armable === false ? palette.materiel : palette.disponible;
    }
    if (v.statut === 'en_mission') return palette.en_mission;
    if (v.statut === 'en_maintenance') return palette.en_maintenance;
    return palette.panne;
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (playerVehicles || [])
      .filter((v) => (buildingFilter === 'all' ? true : Number(v.batiment_id) === buildingFilter))
      .filter((v) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'free') return v.statut === 'disponible';
        if (statusFilter === 'busy') return v.statut === 'en_mission';
        return v.statut === 'en_maintenance' || v.statut === 'panne';
      })
      .filter((v) => {
        if (!q) return true;
        const home = buildingById.get(Number(v.batiment_id))?.nom || '';
        return [v.nom, v.type, home].some((x) => String(x || '').toLowerCase().includes(q));
      });
  }, [playerVehicles, buildingFilter, statusFilter, search, buildingById]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allShown = rows.length > 0 && rows.every((v) => selected.has(Number(v.id)));
  const toggleAll = () =>
    setSelected(allShown ? new Set() : new Set(rows.map((v) => Number(v.id))));

  /** Un groupe sélectionne d'un coup les engins disponibles ET armés du type demandé. */
  const applyGroup = (compo: Record<string, number>) => {
    const picked = new Set<number>();
    for (const [type, need] of Object.entries(compo)) {
      const candidates = rows
        .filter((v) => !picked.has(Number(v.id)))
        .filter((v) => String(v.type || '') === type)
        .filter((v) => v.statut === 'disponible' && crewById.get(Number(v.id))?.armable !== false);
      for (const v of candidates.slice(0, Math.max(0, Number(need) || 0))) picked.add(Number(v.id));
    }
    setSelected(picked);
  };

  const crewCell = (v: Vehicule) => {
    const c = crewById.get(Number(v.id));
    if (!c) return <span style={{ color: P.muted }}>—</span>;
    if (c.engaged) return <span style={{ color: P.muted }}>engagé</span>;
    if (c.armable === false) {
      const motif = c.missing
        .map((m) => `${m.competenceName} — ${m.reason === 'absente' ? 'spécialité absente du centre' : 'titulaire indisponible'}`)
        .join('\n');
      return (
        <span style={{ color: palette.materiel.bg === '#12703A' ? '#34d399' : P.green }} title={motif}>
          {c.missing.map((m) => m.competenceName).join(' · ') || 'équipage incomplet'}
        </span>
      );
    }
    if (c.noCompetenceRequired) return <span style={{ color: P.muted }}>sans équipage requis</span>;
    const names = c.crew.map((id) => {
      const s = staffById[id];
      return s ? `${s.grade ? s.grade + ' ' : ''}${s.prenom} ${s.nom}` : `#${id}`;
    });
    return (
      <span style={{ color: P.text2 }} title={names.join('\n')}>
        {names.length} agent{names.length > 1 ? 's' : ''}
      </span>
    );
  };

  const th: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    background: P.panel2,
    borderBottom: `1px solid ${P.line}`,
    color: P.muted,
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '4px 6px',
    textAlign: 'left',
    fontWeight: 600,
  };
  const td: React.CSSProperties = {
    borderBottom: `1px solid ${P.lineSoft}`,
    padding: '3px 6px',
    fontSize: 11,
    color: P.text,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      className="flex min-h-0 flex-col"
      style={{ background: P.panel, border: `1px solid ${P.line}`, borderRadius: 6 }}
    >
      <div
        className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-2 py-1.5"
        style={{ borderBottom: `1px solid ${P.line}`, background: P.panel2 }}
      >
        <h2
          style={{ color: P.text, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}
          className="uppercase"
        >
          Moniteur d&apos;engins
        </h2>
        <div className="flex flex-wrap items-center gap-1">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Indicatif, type, caserne…"
            className="w-44 px-1.5 py-0.5 text-[10px] outline-none"
            style={{ background: P.panel3, border: `1px solid ${P.line}`, color: P.text, borderRadius: 4 }}
          />
          <select
            value={String(buildingFilter)}
            onChange={(e) => setBuildingFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="px-1 py-0.5 text-[10px] outline-none"
            style={{ background: P.panel3, border: `1px solid ${P.line}`, color: P.text, borderRadius: 4 }}
          >
            <option value="all">Toutes casernes</option>
            {(buildings || []).map((b) => (
              <option key={String(b.id)} value={Number(b.id)}>
                {b.nom}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-1 py-0.5 text-[10px] outline-none"
            style={{ background: P.panel3, border: `1px solid ${P.line}`, color: P.text, borderRadius: 4 }}
          >
            <option value="all">Tous statuts</option>
            <option value="free">Disponibles</option>
            <option value="busy">Engagés</option>
            <option value="ko">Indisponibles</option>
          </select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th style={{ ...th, width: 24 }}>
                <input type="checkbox" checked={allShown} onChange={toggleAll} />
              </th>
              <th style={th}>Indicatif</th>
              <th style={th}>Type</th>
              <th style={th}>Statut</th>
              <th style={th}>Affectation</th>
              <th style={th}>Équipage</th>
              <th style={th}>Remise</th>
              <th style={{ ...th, textAlign: 'right' }}>Délai</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => {
              const st = effStatus(v);
              const home = buildingById.get(Number(v.batiment_id));
              const inv = assignmentOf.get(Number(v.id));
              const eta = etaOf.get(Number(v.id));
              const isSel = selected.has(Number(v.id));
              return (
                <tr key={String(v.id)} style={{ background: isSel ? P.panel3 : undefined }}>
                  <td style={td}>
                    <input type="checkbox" checked={isSel} onChange={() => toggle(Number(v.id))} />
                  </td>
                  <td style={{ ...td, fontFamily: MONO, fontWeight: 700 }}>{v.nom}</td>
                  <td style={{ ...td, color: P.muted }}>{v.type}</td>
                  <td style={td}>
                    <span
                      style={{
                        background: st.bg,
                        color: st.fg,
                        borderRadius: 3,
                        padding: '1px 5px',
                        fontSize: 9,
                        fontWeight: 700,
                      }}
                    >
                      {st.label}
                    </span>
                  </td>
                  <td style={td}>
                    {inv ? (
                      <span style={{ fontFamily: MONO, color: '#fca5a5' }}>
                        {inv.numero_intervention || inv.id}
                      </span>
                    ) : (
                      <span style={{ color: P.muted }}>—</span>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: MONO }}>{crewCell(v)}</td>
                  <td style={{ ...td, color: P.muted }}>
                    {home?.nom || unitTypeLabel(home?.type || '') || '—'}
                  </td>
                  <td style={{ ...td, fontFamily: MONO, textAlign: 'right', color: P.text2 }}>
                    {eta != null && eta > 0 ? `${Math.floor(eta / 60)}:${String(eta % 60).padStart(2, '0')}` : '—'}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...td, textAlign: 'center', color: P.muted, padding: 16 }}>
                  Aucun engin ne correspond au filtre.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div
        className="flex shrink-0 flex-wrap items-center gap-2 px-2 py-1.5"
        style={{ borderTop: `1px solid ${P.line}`, background: P.panel2 }}
      >
        {groups.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            <span style={{ color: P.muted, fontSize: 9 }} className="uppercase tracking-wide">
              Groupes
            </span>
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => applyGroup(g.compo)}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold"
                style={{ background: P.panel3, border: `1px solid ${g.couleur}`, color: P.text, borderRadius: 4 }}
                title={`Sélectionner : ${Object.entries(g.compo).map(([t, n]) => `${t} ×${n}`).join(' · ')}`}
              >
                <span style={{ background: g.couleur, width: 8, height: 8, borderRadius: 2 }} />
                {g.nom}
              </button>
            ))}
          </div>
        ) : null}
        <span style={{ color: P.text2, fontSize: 10 }} className="ml-auto">
          {selected.size} engin{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default UnitsMonitorTable;
